// ============================================================
// ByOurConsent.com -- Civic Action Detail Page
// /civic/:slug
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';
import toast  from 'react-hot-toast';
import useWindowWidth from '../hooks/useWindowWidth';

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

// ============================================================
// Simple markdown renderer
// Handles: [text](url), **bold**, - bullets, auto-links
// ============================================================
const SimpleMarkdown = ({ text }) => {
    if (!text) return null;

    const renderLine = (line, i) => {
        // Bullet list item
        if (line.startsWith('- ') || line.startsWith('* ')) {
            return <li key={i} style={{ marginBottom: '4px' }}>{renderInline(line.slice(2))}</li>;
        }
        // Empty line = paragraph break
        if (!line.trim()) return <br key={i} />;
        return <span key={i}>{renderInline(line)}<br /></span>;
    };

    const renderInline = (text) => {
        // Split on markdown links [text](url) and **bold**
        const parts = [];
        let remaining = text;
        let key = 0;

        while (remaining.length > 0) {
            // Check for [text](url)
            const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
            // Check for **bold**
            const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
            // Check for bare URL
            const urlMatch = remaining.match(/(https?:\/\/[^\s]+)/);

            // Find which comes first
            const matches = [
                linkMatch && { type: 'link', match: linkMatch, index: linkMatch.index },
                boldMatch && { type: 'bold', match: boldMatch, index: boldMatch.index },
                urlMatch  && { type: 'url',  match: urlMatch,  index: urlMatch.index }
            ].filter(Boolean).sort((a, b) => a.index - b.index);

            if (matches.length === 0) {
                parts.push(<span key={key++}>{remaining}</span>);
                break;
            }

            const first = matches[0];
            // Add text before match
            if (first.index > 0) {
                parts.push(<span key={key++}>{remaining.slice(0, first.index)}</span>);
            }

            if (first.type === 'link') {
                parts.push(
                    <a key={key++} href={first.match[2]} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#8b1a1a', textDecoration: 'underline' }}>
                        {first.match[1]}
                    </a>
                );
                remaining = remaining.slice(first.index + first.match[0].length);
            } else if (first.type === 'bold') {
                parts.push(<strong key={key++}>{first.match[1]}</strong>);
                remaining = remaining.slice(first.index + first.match[0].length);
            } else if (first.type === 'url') {
                const url = first.match[1];
                parts.push(
                    <a key={key++} href={url} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#8b1a1a', textDecoration: 'underline', wordBreak: 'break-all' }}>
                        {url}
                    </a>
                );
                remaining = remaining.slice(first.index + url.length);
            }
        }
        return parts;
    };

    const lines = text.split('\n');
    const elements = [];
    let bulletBuffer = [];

    lines.forEach((line, i) => {
        if (line.startsWith('- ') || line.startsWith('* ')) {
            bulletBuffer.push(renderLine(line, i));
        } else {
            if (bulletBuffer.length > 0) {
                elements.push(<ul key={`ul-${i}`} style={{ margin: '8px 0 8px 20px', padding: 0 }}>{bulletBuffer}</ul>);
                bulletBuffer = [];
            }
            elements.push(renderLine(line, i));
        }
    });
    if (bulletBuffer.length > 0) {
        elements.push(<ul key="ul-end" style={{ margin: '8px 0 8px 20px', padding: 0 }}>{bulletBuffer}</ul>);
    }

    return (
        <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>
            {elements}
        </div>
    );
};

// ============================================================
// RepFinder -- find representatives by zip code
// ============================================================
const RepFinder = () => (
    <div>
        <a href="https://www.congress.gov/members/find-your-member" target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-block', background: '#8b1a1a', color: '#f5f0e8', padding: '10px 22px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
            Find my representatives →
        </a>
        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '8px' }}>
            Opens congress.gov — find your senators and representative with phone numbers and contact forms.
        </div>
    </div>
);

const CivicActionPage = () => {
    const { slug } = useParams();
    const width    = useWindowWidth();
    const isMobile = width < 768;
    const [action,  setAction]  = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/civic/${slug}`)
            .then(r => setAction(r.data.action))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8' }}><Nav />
            <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
        </div>
    );

    if (!action) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8' }}><Nav />
            <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Action not found.</div>
        </div>
    );

    const isExpired = action.expires_at && new Date(action.expires_at) < new Date();

    return (
        <>
            <Helmet>
                <title>{action.title} — Take Action — By Our Consent</title>
                <meta name="description" content={action.description} />
            </Helmet>

            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto' }}>
                    <Link to="/civic" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Take action
                    </Link>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: action.level === 'federal' ? '#e8ecf0' : '#f0e8ff', color: action.level === 'federal' ? '#2a4a8a' : '#6a2a8a' }}>
                            {action.level === 'federal' ? '🏛️ Federal' : `📍 ${action.state || 'State'}`}
                        </span>
                        {action.category_name && (
                            <span style={{ fontSize: '9px', background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: '20px', fontFamily: 'sans-serif', color: '#8a9aaa', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                {action.category_name}
                            </span>
                        )}
                        {action.status !== 'active' && (
                            <span style={{ fontSize: '9px', background: 'rgba(139,26,26,0.3)', padding: '2px 8px', borderRadius: '20px', fontFamily: 'sans-serif', color: '#f5e0e0', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                {action.status}
                            </span>
                        )}
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.15, marginBottom: '8px' }}>
                        {action.title}
                    </h1>
                    <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                        Posted {fmtDate(action.created_at)}
                        {action.expires_at && ` · ${isExpired ? 'Expired' : 'Expires'} ${fmtDate(action.expires_at)}`}
                        {` · ${action.view_count} views`}
                    </div>
                </div>
            </div>

            <div style={{ maxWidth: '760px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem 1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 240px', gap: '2rem', alignItems: 'start' }}>

                    {/* Main content */}
                    <div>
                        {/* What's happening */}
                        <div style={{ marginBottom: '2rem' }}>
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px' }}>
                                What's happening
                            </div>
                            <div style={{ fontSize: '16px', fontFamily: 'sans-serif', color: '#2a3040', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                                {action.description}
                            </div>
                        </div>

                        {/* What to do */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderLeft: '3px solid #8b1a1a', borderRadius: '4px', padding: '1.5rem', marginBottom: '2rem' }}>
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b1a1a', marginBottom: '10px', fontWeight: 600 }}>
                                What you can do
                            </div>
                            <div style={{ fontSize: '15px', fontFamily: 'sans-serif', color: '#1e2a3a', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                                {action.what_to_do}
                            </div>
                        </div>

                        {/* More information -- markdown content */}
                        {action.more_info && (
                            <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderLeft: '3px solid #8b6a3a', borderRadius: '4px', padding: '1.5rem', marginBottom: '2rem' }}>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px', fontWeight: 600 }}>
                                    More information
                                </div>
                                <SimpleMarkdown text={action.more_info} />
                            </div>
                        )}

                        {/* Find your representatives -- right after what you can do */}
                        <div id="find-reps" style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderLeft: '3px solid #8b1a1a', borderRadius: '4px', padding: '1.5rem', marginBottom: '2rem' }}>
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b1a1a', marginBottom: '10px', fontWeight: 600 }}>
                                Find your representatives
                            </div>
                            <RepFinder />
                        </div>

                        {action.status !== 'active' && (
                            <div style={{ padding: '1rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                This action is no longer active.
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div>
                        {/* Details card */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1rem' }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '12px', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                                Details
                            </div>
                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif' }}>
                                {[
                                    ['Level', action.level === 'federal' ? '🏛️ Federal' : `📍 ${action.state}`],
                                    ['Category', action.category_name],
                                    ['Status', action.status],
                                    ['Posted', fmtDate(action.created_at)],
                                    ['Expires', action.expires_at ? fmtDate(action.expires_at) : 'No expiry']
                                ].filter(([,v]) => v).map(([label, value]) => (
                                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                                        <span style={{ color: '#8a8070' }}>{label}</span>
                                        <span style={{ color: '#1e2a3a', fontWeight: 600, textAlign: 'right', textTransform: 'capitalize' }}>{value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Share */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem' }}>
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a8070', marginBottom: '10px' }}>
                                Share this action
                            </div>
                            {[
                                { label: 'Share on X', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(action.title)}` },
                                { label: 'Share on Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
                                { label: 'Copy link', url: null }
                            ].map(({ label, url }) => (
                                <button key={label}
                                    onClick={() => {
                                        if (url) window.open(url, '_blank');
                                        else { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
                                    }}
                                    style={{ display: 'block', width: '100%', background: 'transparent', border: '0.5px solid #c8beaa', padding: '7px 12px', fontSize: '11px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '2px', textAlign: 'left', marginBottom: '6px' }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Back link */}
                <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '0.5px solid #c8beaa' }}>
                    <Link to="/civic" style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← All actions
                    </Link>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default CivicActionPage;
